import { useState } from 'react';
import { getDeviceEvent } from '../../../services/apiServices';
import { formatDuration } from '../../../utils/normalizeEvents';
import { ArrowIcon } from '../Icons';

const ACTIONS_NA = new Set(['dock', 'undock']);

// sorter.js
/**
 * @typedef {{ id: string, timestamp: string }} Action
 */

/**
 * Compare two Action objects:
 * 1) Newest timestamp first
 * 2) If timestamps tie, id starting with "mop" wins
 * 3) Finally, lexicographical order of id
 *
 * @param {Action} a
 * @param {Action} b
 * @returns {number}
 */
export function compareByTimestampAndMop(a, b) {
  const timeA = Date.parse(a.timestamp);
  const timeB = Date.parse(b.timestamp);
  const timeDiff = timeB - timeA;
  if (timeDiff !== 0) return timeDiff;

  const aIsMop = a.id.startsWith('mop');
  const bIsMop = b.id.startsWith('mop');
  if (aIsMop !== bIsMop) return aIsMop ? -1 : 1;

  return a.id.localeCompare(b.id);
}

/**
 * Return a new sorted array; original is untouched.
 * @param {Action[]} actions
 * @returns {Action[]}
 */
export function sortActionsByTimestampDesc(actions) {
  if (!Array.isArray(actions)) {
    throw new TypeError('Expected an array of actions');
  }
  return [...actions].sort(compareByTimestampAndMop);
}

/**
 * DetailRow Component
 *
 * Displays an expandable row with nested details. Features a vertical line
 * with a connecting dot and a sub-table structure.
 *
 * Props:
 * - data: { columns: Array<{ event: string, timeStart: string, duration: string }> }
 */
const DetailRow = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [detailData, setDetailData] = useState({});
  const { columns } = data;
  const eventId = columns.find((item) => item.id === 'idEvent')?.value;
  const columnsToShow = columns.filter((item) => item.id !== 'idEvent');

  const onClick = async () => {
    if (isExpanded) {
      setIsExpanded(false);
    } else {
      const results = await getDeviceEvent(eventId);
      const actions = results?.actions || [];
      const sortedActions = sortActionsByTimestampDesc(actions);
      setDetailData(sortedActions);
      setIsExpanded(sortedActions.length);
    }
  };

  return (
    <>
      {/* Main row (clickable) */}
      <tr
        data-testid="detail-row"
        className="bg-white border-b-2 whitespace-nowrap cursor-pointer relative"
        onClick={onClick}
      >
        {columnsToShow.map((column, index) => {
          const showArrow = index === 0;
          return (
            <td
              key={column.id}
              className={`relative py-4 px-4 text-center text-black relative ${
                index === 0 ? 'pl-[50px]' : ''
              }`}
            >
              {showArrow && (
                <div className="relative">
                  <div className="absolute left-[15px]">
                    <ArrowIcon direction={isExpanded ? 'up' : 'down'} />
                  </div>
                </div>
              )}
              {column.value}
            </td>
          );
        })}
      </tr>

      {/* Expanded details row */}
      {isExpanded && (
        <tr className="bg-[#F7F7F7]">
          <td colSpan={columns.length} className="relative px-4">
            <div className="ml-10 relative">
              <div className="absolute left-[24px] top-0 bottom-8 w-[2px] overflow-visible">
                <svg
                  width="2"
                  height="100%"
                  viewBox="0 0 2 100"
                  preserveAspectRatio="none"
                  className="h-full"
                >
                  <path
                    d="M1 0V100"
                    stroke="#C3C3C5"
                    strokeWidth="1"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>

                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2">
                  <svg
                    width="7"
                    height="7"
                    viewBox="0 0 7 7"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="3.5" cy="3.5" r="3.5" fill="#C3C3C5" />
                  </svg>
                </div>
              </div>

              <div className="ml-16 pt-4 pb-8">
                <table className="w-full text-black">
                  <thead>
                    <tr>
                      <th className="text-left py-2 px-4 font-bold text-center">Event</th>
                      <th className="text-left py-2 px-4 font-bold  text-center">Time Start</th>
                      <th className="text-left py-2 px-4 font-bold  text-center">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailData.map((row, index) => {
                      const duration = ACTIONS_NA.has(row.action)
                        ? 'N/A'
                        : formatDuration(row.duration * 1000);

                      return (
                        <tr key={index}>
                          <td className="py-2 px-4 capitalize  text-center">{row.action}</td>
                          <td className="py-2 px-4  text-center">{row.time_range}</td>
                          <td className="py-2 px-4  text-center">{duration}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default DetailRow;
